import { useState, useContext } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performDeleteRequest } from '@atlassian/jira-fetch/src/utils/requests';
import { getStatusCodeGroup } from '@atlassian/jira-project-custom-roles/src/services/utils/index.tsx';
import type { RoleId, BaseUrl, ProjectKey, RoleTypeId, ActorType } from '../../common/types';
import { isUser } from '../../common/utils';
import AccessPageContext from '../access-page-context';
import { isValidationError } from '../utils';
import type { Props } from './types';

export const removeActorFromRoleUrl = (
	baseUrl: BaseUrl,
	projectKey: ProjectKey,
	roleId: RoleId,
	roleTypeId: RoleTypeId,
	type: ActorType,
): string =>
	`${baseUrl}/rest/api/2/project/${projectKey}/role/${roleId}?${
		isUser(type) ? 'user' : 'group'
	}=${encodeURIComponent(roleTypeId)}`;

export const removeActorUrlOptions = () => ({
	method: 'DELETE',
});

export const RemoveActorFromRole = (props: Props) => {
	const { children, onRemoveActorFromRoleError } = props;
	const [error, setError] = useState<unknown>();
	const { baseUrl, projectKey } = useContext(AccessPageContext);

	const removeActorFromRole = async (
		{
			roleId,
			roleTypeId,
			type,
		}: {
			roleId: RoleId;
			roleTypeId: RoleTypeId;
			type: ActorType;
		}, // eslint-disable-next-line @typescript-eslint/no-explicit-any
	): Promise<any> => {
		try {
			await performDeleteRequest(
				removeActorFromRoleUrl(baseUrl, projectKey, roleId, roleTypeId, type),
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (httpError: any) {
			setError(httpError.message);
			onRemoveActorFromRoleError(roleId, roleTypeId, type, isValidationError(httpError)); // if response code is 400, then we assume it is a last admin error
			fireErrorAnalytics({
				meta: {
					id: 'removeActorFromRole',
					packageName: 'jiraUsersAndRoles',
					teamName: 'olympus',
				},
				error: httpError,
				attributes: {
					roleId,
					roleTypeId,
					type,
					statusCode: getStatusCodeGroup(httpError),
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

	return children({
		error,
		onRemoveActorFromRole: removeActorFromRole,
	});
};

export default RemoveActorFromRole;
